import React, { forwardRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';

// Pixel-Perfect Government Style Certificate - Exact Reference Match
const PremiumCertificate = forwardRef(({ certificate, settings, studentPhoto }, ref) => {
  
  const IQRA_LOGO = settings?.iqra_logo_url || 'https://customer-assets.emergentagent.com/job_2f370b5d-868a-4240-92b5-92a3302f8c5a/artifacts/atupje9x_1%20copy%20-%20Copy.png';
  const SKILL_INDIA_LOGO = settings?.skill_india_logo_url || 'https://customer-assets.emergentagent.com/job_2f370b5d-868a-4240-92b5-92a3302f8c5a/artifacts/iym3ivrf_Untitled-1%201.png';
  const SIGNATURE_URL = settings?.signatory_signature_url;
  
  // Student photo from certificate data or passed prop
  const STUDENT_PHOTO = studentPhoto || certificate?.photo_url || certificate?.student_photo || certificate?.profile_image;

  if (!certificate) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const verificationUrl = settings?.verification_base_url 
    ? `${settings.verification_base_url}/verify/${encodeURIComponent(certificate.certificate_number)}`
    : `${window.location.origin}/verify/${encodeURIComponent(certificate.certificate_number)}`;

  // Golden corner ornament SVG - Exact reference match
  const CornerOrnament = ({ style }) => (
    <svg style={style} width="100" height="100" viewBox="0 0 120 120">
      <defs>
        <linearGradient id="goldGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#D4AF37"/>
          <stop offset="30%" stopColor="#F5D061"/>
          <stop offset="60%" stopColor="#C9A227"/>
          <stop offset="100%" stopColor="#B8860B"/>
        </linearGradient>
      </defs>
      {/* Main curved flourish */}
      <path d="M10,10 Q10,60 60,60 Q60,10 110,10" fill="none" stroke="url(#goldGrad)" strokeWidth="2.5"/>
      <path d="M10,10 Q60,10 60,60 Q10,60 10,10" fill="none" stroke="url(#goldGrad)" strokeWidth="1.5"/>
      {/* Inner decorative curves */}
      <path d="M15,15 C35,15 35,35 55,35" fill="none" stroke="#C9A227" strokeWidth="1.2"/>
      <path d="M15,15 C15,35 35,35 35,55" fill="none" stroke="#C9A227" strokeWidth="1.2"/>
      {/* Decorative dots */}
      <circle cx="25" cy="25" r="4" fill="#D4AF37"/>
      <circle cx="40" cy="18" r="2.5" fill="#C9A227"/>
      <circle cx="18" cy="40" r="2.5" fill="#C9A227"/>
      <circle cx="45" cy="30" r="2" fill="#B8860B"/>
      <circle cx="30" cy="45" r="2" fill="#B8860B"/>
      {/* Additional flourishes */}
      <path d="M20,50 Q30,50 35,55" fill="none" stroke="#D4AF37" strokeWidth="1"/>
      <path d="M50,20 Q50,30 55,35" fill="none" stroke="#D4AF37" strokeWidth="1"/>
    </svg>
  );

  return (
    <div 
      ref={ref}
      className="certificate-container"
      style={{
        width: '297mm',
        minHeight: '210mm',
        background: '#ffffff',
        position: 'relative',
        fontFamily: "'Times New Roman', 'Noto Sans Devanagari', serif",
        boxSizing: 'border-box',
        padding: '6mm',
        color: '#000'
      }}
    >
      {/* Outer Golden Border */}
      <div style={{
        position: 'absolute',
        top: '5mm',
        left: '5mm',
        right: '5mm',
        bottom: '5mm',
        border: '2px solid #D4AF37',
        pointerEvents: 'none'
      }}>
        {/* Inner Border */}
        <div style={{
          position: 'absolute',
          top: '3mm',
          left: '3mm',
          right: '3mm',
          bottom: '3mm',
          border: '1px solid #C9A227'
        }} />
      </div>

      {/* Corner Ornaments */}
      <CornerOrnament style={{ position: 'absolute', top: '2mm', left: '2mm' }} />
      <CornerOrnament style={{ position: 'absolute', top: '2mm', right: '2mm', transform: 'scaleX(-1)' }} />
      <CornerOrnament style={{ position: 'absolute', bottom: '2mm', left: '2mm', transform: 'scaleY(-1)' }} />
      <CornerOrnament style={{ position: 'absolute', bottom: '2mm', right: '2mm', transform: 'scale(-1)' }} />

      {/* Center Watermark - Only Logo, No Text */}
      <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        opacity: 0.08,
        pointerEvents: 'none',
        zIndex: 0
      }}>
        <img 
          src={SKILL_INDIA_LOGO} 
          alt="" 
          style={{ width: '100mm', height: 'auto' }} 
        />
      </div>

      {/* Main Content */}
      <div style={{ position: 'relative', zIndex: 1, padding: '8mm 12mm 6mm' }}>
        
        {/* Header Section */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4mm' }}>
          
          {/* Left - IQRA Logo */}
          <div style={{ width: '50mm', textAlign: 'center', paddingTop: '5mm' }}>
            <img 
              src={IQRA_LOGO} 
              alt="IQRA Skills" 
              style={{ height: '34mm', objectFit: 'contain' }} 
            />
          </div>

          {/* Center - Skill India Logo & Title */}
          <div style={{ flex: 1, textAlign: 'center', padding: '0 5mm' }}>
            <img 
              src={SKILL_INDIA_LOGO} 
              alt="Skill India" 
              style={{ height: '40mm', objectFit: 'contain' }} 
            />
            <div style={{ 
              fontSize: '14px', 
              fontWeight: 'bold', 
              color: '#006633',
              marginTop: '3mm'
            }}>
              कौशल योग्यता प्रमाणपत्र
            </div>
            <div style={{ 
              fontSize: '12px', 
              color: '#006633',
              fontWeight: '500'
            }}>
              Certificate for Skill Competency
            </div>
          </div>

          {/* Right - Photo & Certificate No */}
          <div style={{ width: '40mm', textAlign: 'right' }}>
            {/* Student Photo */}
            <div style={{
              width: '30mm',
              height: '36mm',
              border: '1px solid #999',
              marginLeft: 'auto',
              marginBottom: '2mm',
              overflow: 'hidden',
              background: '#f5f5f5'
            }}>
              {STUDENT_PHOTO ? (
                <img 
                  src={STUDENT_PHOTO} 
                  alt="Student" 
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <div style={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '8px',
                  color: '#999'
                }}>
                  Photo
                </div>
              )}
            </div>
            <div style={{ fontSize: '8px', color: '#333', textAlign: 'right' }}>
              <span style={{ color: '#006633' }}>प्रमाणपत्रसंख्या</span><br/>
              <span style={{ color: '#cc0000' }}>CertificateNo:</span><br/>
              <strong style={{ fontSize: '10px' }}>{certificate.certificate_number}</strong>
            </div>
          </div>
        </div>

        {/* Certificate Body - Form Style Layout - Hindi on top, English below */}
        <div style={{ marginTop: '5mm', fontSize: '10px', lineHeight: '1.5' }}>
          
          {/* Row 1: Name */}
          <div style={{ color: '#006633', fontSize: '10px' }}>प्रमाणित किया जाता है कि श्री / सुश्री / एमएक्स</div>
          <div style={{ display: 'flex', alignItems: 'baseline' }}>
            <span style={{ color: '#cc0000', fontSize: '10px' }}>This is to certify that Mr./Ms./Mx.</span>
            <span style={{ 
              flex: 1, 
              borderBottom: '1px solid #000', 
              fontWeight: 'bold',
              fontSize: '13px',
              paddingLeft: '2mm',
              marginLeft: '2mm'
            }}>
              {certificate.student_name}
            </span>
          </div>

          {/* Row 2: Father's Name, DOB, Enrollment - Hindi on top */}
          <div style={{ display: 'flex', marginTop: '3mm', gap: '5mm' }}>
            {/* Father Name */}
            <div style={{ flex: 1 }}>
              <div style={{ color: '#006633', fontSize: '9px' }}>पुत्र</div>
              <div style={{ display: 'flex', alignItems: 'baseline' }}>
                <span style={{ color: '#cc0000', fontSize: '9px' }}>Son of</span>
                <span style={{ flex: 1, borderBottom: '1px solid #000', marginLeft: '2mm', paddingLeft: '2mm', fontWeight: '500' }}>
                  {certificate.father_name || 'N/A'}
                </span>
              </div>
            </div>
            {/* DOB */}
            <div style={{ width: '55mm' }}>
              <div style={{ color: '#006633', fontSize: '9px' }}>जन्म तिथि</div>
              <div style={{ display: 'flex', alignItems: 'baseline' }}>
                <span style={{ color: '#cc0000', fontSize: '9px' }}>Date of Birth</span>
                <span style={{ flex: 1, borderBottom: '1px solid #000', marginLeft: '2mm', paddingLeft: '2mm' }}>
                  {certificate.date_of_birth || formatDate(certificate.dob)}
                </span>
              </div>
            </div>
            {/* Enrollment */}
            <div style={{ width: '55mm' }}>
              <div style={{ color: '#006633', fontSize: '9px' }}>नामांकन संख्या</div>
              <div style={{ display: 'flex', alignItems: 'baseline' }}>
                <span style={{ color: '#cc0000', fontSize: '9px' }}>Enrolment No</span>
                <span style={{ flex: 1, borderBottom: '1px solid #000', marginLeft: '2mm', paddingLeft: '2mm' }}>
                  {certificate.enrollment_number}
                </span>
              </div>
            </div>
          </div>

          {/* Row 3: Assessment Cleared + Duration at end (aligned with Enrollment) */}
          <div style={{ marginTop: '3mm' }}>
            <div style={{ display: 'flex', gap: '5mm' }}>
              <div style={{ flex: 1 }}>
                <div style={{ color: '#006633', fontSize: '10px' }}>ने उत्तीर्ण रोल / उत्तीर्ण का मूल्यांकन सफलतापूर्वक</div>
                <div style={{ display: 'flex', alignItems: 'baseline' }}>
                  <span style={{ color: '#cc0000', fontSize: '10px' }}>has successfully cleared the assessment in the job role/qualification</span>
                  <span style={{ flex: 1, borderBottom: '1px solid #000', marginLeft: '2mm', paddingLeft: '2mm', fontWeight: 'bold', fontSize: '12px', color: '#000080' }}>
                    {certificate.course_name}
                  </span>
                </div>
              </div>
              {/* Duration - aligned with Enrollment No */}
              <div style={{ width: '55mm' }}>
                <div style={{ color: '#006633', fontSize: '9px' }}>अवधि</div>
                <div style={{ display: 'flex', alignItems: 'baseline' }}>
                  <span style={{ color: '#cc0000', fontSize: '9px' }}>of Duration</span>
                  <span style={{ flex: 1, borderBottom: '1px solid #000', marginLeft: '2mm', paddingLeft: '2mm', fontWeight: '500' }}>
                    {certificate.duration || '180 Hrs'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Row 4: Training Centre, District, State - Hindi on top */}
          <div style={{ display: 'flex', marginTop: '3mm', gap: '5mm' }}>
            {/* Training Centre */}
            <div style={{ flex: 1 }}>
              <div style={{ color: '#006633', fontSize: '8px' }}>प्रशिक्षण केन्द्र</div>
              <div style={{ display: 'flex', alignItems: 'baseline' }}>
                <span style={{ color: '#cc0000', fontSize: '8px' }}>Training Centre</span>
                <span style={{ flex: 1, borderBottom: '1px solid #000', marginLeft: '2mm', paddingLeft: '2mm', fontWeight: '500', fontSize: '9px', textTransform: 'uppercase' }}>
                  {certificate.center_name || certificate.training_center || 'IQRA CAD INSTITUTE'}
                </span>
              </div>
            </div>
            {/* District */}
            <div style={{ width: '50mm' }}>
              <div style={{ color: '#006633', fontSize: '8px' }}>जिला</div>
              <div style={{ display: 'flex', alignItems: 'baseline' }}>
                <span style={{ color: '#cc0000', fontSize: '8px' }}>District</span>
                <span style={{ flex: 1, borderBottom: '1px solid #000', marginLeft: '2mm', paddingLeft: '2mm', fontSize: '9px' }}>
                  {certificate.district}
                </span>
              </div>
            </div>
            {/* State */}
            <div style={{ width: '50mm' }}>
              <div style={{ color: '#006633', fontSize: '8px' }}>राज्य</div>
              <div style={{ display: 'flex', alignItems: 'baseline' }}>
                <span style={{ color: '#cc0000', fontSize: '8px' }}>State</span>
                <span style={{ flex: 1, borderBottom: '1px solid #000', marginLeft: '2mm', paddingLeft: '2mm', fontSize: '9px', textTransform: 'uppercase' }}>
                  {certificate.state}
                </span>
              </div>
            </div>
          </div>

          {/* Row 6: Grade - Hindi on top */}
          <div style={{ marginTop: '3mm', width: '100mm' }}>
            <div style={{ color: '#006633', fontSize: '9px' }}>प्रतिशत / श्रेणी के साथ उत्तीर्ण किया।</div>
            <div style={{ display: 'flex', alignItems: 'baseline' }}>
              <span style={{ color: '#cc0000', fontSize: '9px' }}>with</span>
              <span style={{ 
                width: '15mm', 
                borderBottom: '1px solid #000',
                marginLeft: '2mm',
                paddingLeft: '2mm',
                fontWeight: 'bold',
                fontSize: '14px',
                textAlign: 'center',
                color: certificate.grade === 'A' || certificate.grade === 'A+' ? '#006633' : '#000'
              }}>
                {certificate.percentage ? `${Number(certificate.percentage).toFixed(0)}%` : ''} {certificate.grade}
              </span>
              <span style={{ fontSize: '9px', marginLeft: '2mm' }}>%/Grade</span>
            </div>
          </div>

          {/* Row 7: Place of Issue - Hindi on top */}
          <div style={{ marginTop: '3mm', width: '100mm' }}>
            <div style={{ color: '#006633', fontSize: '9px' }}>जारी करने का स्थान</div>
            <div style={{ display: 'flex', alignItems: 'baseline' }}>
              <span style={{ color: '#cc0000', fontSize: '9px' }}>Place of Issue:</span>
              <span style={{ width: '40mm', borderBottom: '1px solid #000', marginLeft: '2mm', paddingLeft: '2mm' }}>
                {certificate.place_of_issue || settings?.default_place || 'Rajasthan'}
              </span>
            </div>
          </div>

          {/* Row 8: Date of Issue - Hindi on top */}
          <div style={{ marginTop: '2mm', width: '80mm' }}>
            <div style={{ color: '#006633', fontSize: '9px' }}>जारी करने की तिथि</div>
            <div style={{ display: 'flex', alignItems: 'baseline' }}>
              <span style={{ color: '#cc0000', fontSize: '9px' }}>Date of Issue:</span>
              <span style={{ width: '30mm', borderBottom: '1px solid #000', marginLeft: '2mm', paddingLeft: '2mm' }}>
                {formatDate(certificate.date_of_issue)}
              </span>
            </div>
          </div>
        </div>

        {/* Bottom Section - QR Code and Signature */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-end',
          marginTop: '8mm',
          paddingTop: '3mm'
        }}>
          
          {/* QR Code */}
          <div style={{ textAlign: 'left' }}>
            <QRCodeSVG 
              value={verificationUrl}
              size={80}
              level="M"
              includeMargin={false}
              style={{ border: '1px solid #ccc' }}
            />
          </div>

          {/* Seal & Signature Area */}
          <div style={{ textAlign: 'right', minWidth: '60mm' }}>
            {SIGNATURE_URL && (
              <div style={{ marginBottom: '2mm' }}>
                <img 
                  src={SIGNATURE_URL} 
                  alt="Signature" 
                  style={{ height: '15mm', objectFit: 'contain' }}
                />
              </div>
            )}
            <div style={{ fontSize: '11px', fontWeight: 'bold', marginBottom: '2mm' }}>
              Seal & Sign
            </div>
            <div style={{ 
              fontSize: '11px', 
              fontWeight: 'bold',
              borderTop: '1px solid #000',
              paddingTop: '2mm',
              display: 'inline-block',
              minWidth: '40mm'
            }}>
              {settings?.signatory_name || 'Director'}
            </div>
            <div style={{ fontSize: '10px', marginTop: '3mm' }}>
              Date: {formatDate(certificate.date_of_issue)}
            </div>
            <div style={{ fontSize: '10px', marginTop: '1mm' }}>
              Place: {certificate.place_of_issue || certificate.district || settings?.default_place}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

PremiumCertificate.displayName = 'PremiumCertificate';

export default PremiumCertificate;
